'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { motion, AnimatePresence } from 'framer-motion'
import { BookOpen, CheckCircle2, XCircle } from 'lucide-react'

interface TriviaQuestion {
  question: string
  options: string[]
  correctAnswer: number
  explanation: string
}

const triviaQuestions: TriviaQuestion[] = [
  {
    question: "Who built the ark to survive the great flood?",
    options: ["Moses", "Noah", "Abraham", "David"],
    correctAnswer: 1,
    explanation: "Noah built the ark as God instructed him, saving his family and animals from the flood."
  },
  {
    question: "How many days did Jesus fast in the wilderness?",
    options: ["7 days", "30 days", "40 days", "50 days"],
    correctAnswer: 2,
    explanation: "Jesus fasted for 40 days and 40 nights in the wilderness before being tempted by Satan."
  },
  {
    question: "Who was swallowed by a great fish?",
    options: ["Jonah", "Job", "Joshua", "Joel"],
    correctAnswer: 0,
    explanation: "Jonah was swallowed by a great fish after fleeing from God's command to go to Nineveh."
  },
  {
    question: "How many apostles did Jesus choose?",
    options: ["10", "11", "12", "13"],
    correctAnswer: 2,
    explanation: "Jesus chose 12 apostles to be His closest disciples and spread His teachings."
  },
  {
    question: "What was the first miracle Jesus performed?",
    options: ["Healing a blind man", "Walking on water", "Turning water into wine", "Feeding 5,000"],
    correctAnswer: 2,
    explanation: "Jesus turned water into wine at the wedding in Cana, as recorded in John 2:1-11."
  },
  {
    question: "Who betrayed Jesus for 30 pieces of silver?",
    options: ["Peter", "Judas", "Thomas", "James"],
    correctAnswer: 1,
    explanation: "Judas Iscariot betrayed Jesus to the chief priests for thirty pieces of silver."
  },
  {
    question: "What did God create on the first day?",
    options: ["Animals", "Humans", "Light", "Plants"],
    correctAnswer: 2,
    explanation: "God said 'Let there be light,' and there was light on the first day of creation."
  },
  {
    question: "Who was the strongest man in the Bible?",
    options: ["Goliath", "David", "Samson", "Moses"],
    correctAnswer: 2,
    explanation: "Samson possessed supernatural strength given by God, as long as he kept his Nazirite vows."
  },
  {
    question: "How many plagues did God send to Egypt?",
    options: ["7", "10", "12", "15"],
    correctAnswer: 1,
    explanation: "God sent 10 plagues upon Egypt to convince Pharaoh to let the Israelites go."
  },
  {
    question: "Who was the first king of Israel?",
    options: ["David", "Solomon", "Saul", "Samuel"],
    correctAnswer: 2,
    explanation: "Saul was anointed by Samuel as the first king of Israel."
  },
  {
    question: "What is the shortest verse in the Bible?",
    options: ["God is love", "Jesus wept", "Rejoice always", "Pray continually"],
    correctAnswer: 1,
    explanation: "Jesus wept (John 11:35) is the shortest verse in the Bible."
  },
  {
    question: "Who wrote most of the New Testament epistles?",
    options: ["Peter", "John", "Paul", "James"],
    correctAnswer: 2,
    explanation: "Paul wrote at least 13 of the 27 New Testament books."
  },
  {
    question: "What did Jacob give to Joseph that made his brothers jealous?",
    options: ["A ring", "A coat of many colors", "A sword", "A crown"],
    correctAnswer: 1,
    explanation: "Jacob gave Joseph a richly ornamented coat, showing his favoritism."
  },
  {
    question: "How many books are in the Bible?",
    options: ["50", "66", "73", "81"],
    correctAnswer: 1,
    explanation: "The Protestant Bible contains 66 books: 39 in the Old Testament and 27 in the New Testament."
  },
  {
    question: "Who led the Israelites out of Egypt?",
    options: ["Aaron", "Moses", "Joshua", "Abraham"],
    correctAnswer: 1,
    explanation: "Moses led the Israelites out of slavery in Egypt through God's mighty power."
  },
  {
    question: "What sea did Moses part?",
    options: ["Dead Sea", "Mediterranean Sea", "Red Sea", "Sea of Galilee"],
    correctAnswer: 2,
    explanation: "Moses parted the Red Sea, allowing the Israelites to escape from Pharaoh's army."
  },
  {
    question: "Who defeated Goliath?",
    options: ["Saul", "David", "Jonathan", "Samuel"],
    correctAnswer: 1,
    explanation: "Young David defeated the giant Goliath with a sling and a stone."
  },
  {
    question: "How many brothers did Joseph have?",
    options: ["7", "9", "11", "13"],
    correctAnswer: 2,
    explanation: "Joseph had 11 brothers, making him one of Jacob's 12 sons."
  },
  {
    question: "What was Matthew's profession before following Jesus?",
    options: ["Fisherman", "Tax collector", "Carpenter", "Shepherd"],
    correctAnswer: 1,
    explanation: "Matthew was a tax collector before Jesus called him to be a disciple."
  },
  {
    question: "Who was the mother of Jesus?",
    options: ["Mary", "Martha", "Elizabeth", "Ruth"],
    correctAnswer: 0,
    explanation: "Mary, a virgin, was chosen by God to be the mother of Jesus."
  },
  {
    question: "What animal spoke to Balaam?",
    options: ["A serpent", "A donkey", "A raven", "A dove"],
    correctAnswer: 1,
    explanation: "God caused Balaam's donkey to speak to him in Numbers 22."
  },
  {
    question: "How many days was Lazarus dead before Jesus raised him?",
    options: ["1 day", "2 days", "3 days", "4 days"],
    correctAnswer: 3,
    explanation: "Lazarus had been in the tomb for four days before Jesus raised him from the dead."
  },
  {
    question: "Who was the first martyr of the Christian church?",
    options: ["Peter", "Paul", "Stephen", "James"],
    correctAnswer: 2,
    explanation: "Stephen was stoned to death, making him the first Christian martyr recorded in Acts."
  },
  {
    question: "What did Jesus ride into Jerusalem on Palm Sunday?",
    options: ["A horse", "A camel", "A donkey", "He walked"],
    correctAnswer: 2,
    explanation: "Jesus rode a donkey into Jerusalem, fulfilling the prophecy in Zechariah 9:9."
  },
  {
    question: "How many loaves and fish did Jesus use to feed 5,000?",
    options: ["3 loaves, 2 fish", "5 loaves, 2 fish", "7 loaves, 3 fish", "2 loaves, 5 fish"],
    correctAnswer: 1,
    explanation: "Jesus fed 5,000 men (plus women and children) with just 5 loaves and 2 fish."
  },
  {
    question: "Who denied Jesus three times?",
    options: ["Judas", "Peter", "Thomas", "John"],
    correctAnswer: 1,
    explanation: "Peter denied knowing Jesus three times before the rooster crowed, as Jesus predicted."
  },
  {
    question: "What city was Jesus born in?",
    options: ["Nazareth", "Jerusalem", "Bethlehem", "Capernaum"],
    correctAnswer: 2,
    explanation: "Jesus was born in Bethlehem, fulfilling the prophecy of Micah 5:2."
  },
  {
    question: "How many commandments did Moses receive?",
    options: ["5", "10", "12", "15"],
    correctAnswer: 1,
    explanation: "Moses received the Ten Commandments from God on Mount Sinai."
  },
  {
    question: "Who was the wisest man in the Bible?",
    options: ["David", "Solomon", "Daniel", "Joseph"],
    correctAnswer: 1,
    explanation: "God granted Solomon wisdom beyond any other person, as recorded in 1 Kings."
  },
  {
    question: "What did God use to destroy Sodom and Gomorrah?",
    options: ["Flood", "Earthquake", "Fire and brimstone", "Plague"],
    correctAnswer: 2,
    explanation: "God rained down fire and brimstone (sulfur) to destroy the wicked cities."
  },
  {
    question: "Who was thrown into a lion's den?",
    options: ["Daniel", "David", "Jeremiah", "Ezekiel"],
    correctAnswer: 0,
    explanation: "Daniel was thrown into the lion's den but was protected by God."
  },
  {
    question: "What was Paul's name before his conversion?",
    options: ["Simon", "Saul", "Samuel", "Stephen"],
    correctAnswer: 1,
    explanation: "Before his conversion on the road to Damascus, Paul was known as Saul."
  },
  {
    question: "How many days and nights did it rain during the flood?",
    options: ["7", "30", "40", "100"],
    correctAnswer: 2,
    explanation: "It rained for 40 days and 40 nights during Noah's flood."
  },
  {
    question: "Who was the oldest person in the Bible?",
    options: ["Noah", "Adam", "Methuselah", "Abraham"],
    correctAnswer: 2,
    explanation: "Methuselah lived to be 969 years old, making him the oldest person in the Bible."
  },
  {
    question: "What did God ask Abraham to sacrifice?",
    options: ["A lamb", "His son Isaac", "His wife Sarah", "His firstborn animal"],
    correctAnswer: 1,
    explanation: "God tested Abraham by asking him to sacrifice his son Isaac, but provided a ram instead."
  },
  {
    question: "Who was the first person to see Jesus after His resurrection?",
    options: ["Peter", "John", "Mary Magdalene", "Thomas"],
    correctAnswer: 2,
    explanation: "Mary Magdalene was the first to see the risen Jesus at the tomb."
  },
  {
    question: "What did John the Baptist eat in the wilderness?",
    options: ["Bread and fish", "Locusts and wild honey", "Figs and dates", "Manna from heaven"],
    correctAnswer: 1,
    explanation: "John the Baptist ate locusts and wild honey while preaching in the wilderness."
  },
  {
    question: "How many people were on Noah's ark?",
    options: ["2", "4", "8", "12"],
    correctAnswer: 2,
    explanation: "Noah, his wife, their three sons, and their sons' wives - 8 people total."
  },
  {
    question: "Who was the first person to be created by God?",
    options: ["Eve", "Adam", "Cain", "Abel"],
    correctAnswer: 1,
    explanation: "Adam was formed from the dust of the ground, making him the first human."
  },
  {
    question: "What was the sign of God's covenant with Noah?",
    options: ["A dove", "A rainbow", "An olive branch", "A cloud"],
    correctAnswer: 1,
    explanation: "God set a rainbow in the sky as a sign of His covenant never to flood the earth again."
  },
  {
    question: "Who was the wife of Abraham?",
    options: ["Rachel", "Leah", "Sarah", "Rebekah"],
    correctAnswer: 2,
    explanation: "Sarah was Abraham's wife and the mother of Isaac."
  },
  {
    question: "What did the dove bring back to Noah?",
    options: ["A twig", "An olive leaf", "A flower", "Nothing"],
    correctAnswer: 1,
    explanation: "The dove returned with a freshly plucked olive leaf, showing the waters had receded."
  },
  {
    question: "How many sons did Jacob have?",
    options: ["7", "10", "12", "14"],
    correctAnswer: 2,
    explanation: "Jacob had 12 sons who became the fathers of the 12 tribes of Israel."
  },
  {
    question: "Who wrote the most Psalms?",
    options: ["Solomon", "Moses", "David", "Asaph"],
    correctAnswer: 2,
    explanation: "David wrote approximately 73 of the 150 Psalms in the Book of Psalms."
  },
  {
    question: "What did Jesus say was the greatest commandment?",
    options: ["Do not murder", "Love the Lord your God", "Honor your parents", "Do not steal"],
    correctAnswer: 1,
    explanation: "Jesus said to love the Lord your God with all your heart, soul, and mind (Matthew 22:37-38)."
  },
  {
    question: "Who was the brother of Moses?",
    options: ["Joshua", "Aaron", "Caleb", "Miriam"],
    correctAnswer: 1,
    explanation: "Aaron was Moses' older brother and served as the first high priest of Israel."
  },
  {
    question: "What fruit did Adam and Eve eat in the Garden?",
    options: ["Apple", "Fig", "Grape", "The Bible doesn't specify"],
    correctAnswer: 3,
    explanation: "The Bible only mentions 'fruit from the tree of knowledge of good and evil,' not a specific type."
  },
  {
    question: "Who was the Roman governor who sentenced Jesus to death?",
    options: ["Herod", "Caesar", "Pilate", "Felix"],
    correctAnswer: 2,
    explanation: "Pontius Pilate was the Roman governor who ordered Jesus' crucifixion."
  },
  {
    question: "What was Jesus' first miracle?",
    options: ["Healing the sick", "Turning water to wine", "Walking on water", "Raising the dead"],
    correctAnswer: 1,
    explanation: "Jesus turned water into wine at a wedding in Cana of Galilee."
  },
  {
    question: "How many Gospels are in the New Testament?",
    options: ["2", "3", "4", "5"],
    correctAnswer: 2,
    explanation: "There are four Gospels: Matthew, Mark, Luke, and John."
  }
]

export default function NotFound() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [selectedQuestions, setSelectedQuestions] = useState<TriviaQuestion[]>([])
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null)
  const [score, setScore] = useState(0)
  const [gameStarted, setGameStarted] = useState(false)
  const [gameComplete, setGameComplete] = useState(false)

  useEffect(() => {
    // Select 3 random questions when component mounts
    const shuffled = [...triviaQuestions].sort(() => Math.random() - 0.5)
    setSelectedQuestions(shuffled.slice(0, 3))
  }, [])

  const startGame = () => {
    setGameStarted(true)
    setCurrentQuestionIndex(0)
    setSelectedAnswer(null)
    setIsCorrect(null)
    setScore(0)
    setGameComplete(false)
  }

  const handleAnswerSelect = (answerIndex: number) => {
    if (selectedAnswer !== null) return // Already answered

    setSelectedAnswer(answerIndex)
    const correct = answerIndex === selectedQuestions[currentQuestionIndex].correctAnswer
    setIsCorrect(correct)

    if (correct) {
      setScore(score + 1)
    }
  }

  const handleNextQuestion = () => {
    if (currentQuestionIndex < selectedQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
      setSelectedAnswer(null)
      setIsCorrect(null)
    } else {
      setGameComplete(true)
    }
  }

  const resetGame = () => {
    const shuffled = [...triviaQuestions].sort(() => Math.random() - 0.5)
    setSelectedQuestions(shuffled.slice(0, 3))
    setGameStarted(false)
    setCurrentQuestionIndex(0)
    setSelectedAnswer(null)
    setIsCorrect(null)
    setScore(0)
    setGameComplete(false)
  }

  if (selectedQuestions.length === 0) {
    return null // Loading
  }

  const currentQuestion = selectedQuestions[currentQuestionIndex]

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-secondary/20 flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="text-8xl font-bold text-foreground mb-4">404</h1>
        <h2 className="text-3xl font-semibold text-foreground mb-2">
          Page Not Found
        </h2>
        <p className="text-lg text-muted-foreground max-w-md mx-auto">
          The page you&apos;re looking for has wandered off... but while you&apos;re here, test your Bible knowledge!
        </p>
      </motion.div>

      {/* Bible Trivia Game */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-background to-secondary/30 backdrop-blur-sm rounded-2xl shadow-2xl border border-border/50 p-6 md:p-8 max-w-2xl w-full"
      >
        <AnimatePresence mode="wait">
          {!gameStarted && !gameComplete && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-8"
            >
              <div className="inline-flex rounded-xl bg-primary/10 p-4 mb-6">
                <BookOpen className="h-12 w-12 text-primary" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Bible Trivia Challenge
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Test your knowledge of Scripture with 3 random questions. Can you get a perfect score?
              </p>
              <p className="text-sm text-muted-foreground italic mb-8">
                &quot;Your word is a lamp to my feet and a light to my path.&quot; - Psalm 119:105
              </p>
              <Button onClick={startGame} size="lg">
                Start Challenge
              </Button>
            </motion.div>
          )}

          {gameStarted && !gameComplete && (
            <motion.div
              key="question"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="flex justify-between items-center mb-6">
                <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-primary">
                  <BookOpen className="h-3 w-3" />
                  Question {currentQuestionIndex + 1} of {selectedQuestions.length}
                </span>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Score: {score}/{selectedQuestions.length}</p>
                </div>
              </div>

              <h3 className="text-xl md:text-2xl font-bold text-foreground mb-6">
                {currentQuestion.question}
              </h3>

              <div className="space-y-3 mb-6">
                {currentQuestion.options.map((option, index) => {
                  const isSelected = selectedAnswer === index
                  const isCorrectAnswer = index === currentQuestion.correctAnswer
                  const showResult = selectedAnswer !== null

                  let buttonClasses = "w-full text-left p-4 rounded-xl border-2 transition-all duration-200 "

                  if (!showResult) {
                    buttonClasses += "border-border bg-secondary/30 hover:border-primary/50 hover:bg-primary/5"
                  } else if (isSelected && isCorrectAnswer) {
                    buttonClasses += "border-green-500 bg-green-500/10"
                  } else if (isSelected && !isCorrectAnswer) {
                    buttonClasses += "border-red-500 bg-red-500/10"
                  } else if (isCorrectAnswer) {
                    buttonClasses += "border-green-500 bg-green-500/10"
                  } else {
                    buttonClasses += "border-border bg-secondary/20 opacity-50"
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                      className={buttonClasses}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-foreground font-medium">{option}</span>
                        {showResult && isCorrectAnswer && (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        )}
                        {showResult && isSelected && !isCorrectAnswer && (
                          <XCircle className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>

              {selectedAnswer !== null && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div className={`rounded-xl p-4 ${isCorrect ? 'bg-green-500/10 border border-green-500/20' : 'bg-blue-500/10 border border-blue-500/20'}`}>
                    <p className="text-sm text-foreground">
                      {currentQuestion.explanation}
                    </p>
                  </div>
                  <Button onClick={handleNextQuestion} className="w-full" size="lg">
                    {currentQuestionIndex < selectedQuestions.length - 1 ? "Next Question" : "See Results"}
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}

          {gameComplete && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="text-6xl mb-6">
                {score === 3 ? "🎉" : score === 2 ? "👏" : "📖"}
              </div>
              <h3 className="text-3xl font-bold text-foreground mb-4">
                {score === 3 ? "Perfect Score!" : score === 2 ? "Well Done!" : "Good Effort!"}
              </h3>
              <p className="text-xl text-muted-foreground mb-2">
                You scored {score} out of {selectedQuestions.length}
              </p>
              <p className="text-sm text-muted-foreground italic mb-8">
                {score === 3
                  ? "&quot;Study to show yourself approved unto God.&quot; - 2 Timothy 2:15"
                  : score === 2
                  ? "&quot;The Lord gives wisdom; from his mouth come knowledge and understanding.&quot; - Proverbs 2:6"
                  : "&quot;Desire the pure milk of the word, that you may grow.&quot; - 1 Peter 2:2"}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={resetGame} variant="outline" size="lg">
                  Play Again
                </Button>
                <Link href="/">
                  <Button size="lg">Go Home</Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Navigation */}
      {!gameComplete && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
        >
          <Link href="/">
            <Button variant="outline" size="lg">
              Skip to Home
            </Button>
          </Link>
        </motion.div>
      )}

      {/* Background decoration */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
      </div>
    </div>
  )
}
